import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  Youtube, Instagram, Facebook, Music2, RefreshCw, Link2, Unlink, 
  CheckCircle, AlertCircle, Users, Eye, Heart, Video
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = {
  youtube: { name: 'YouTube', icon: Youtube, color: 'bg-red-500', available: true },
  instagram: { name: 'Instagram', icon: Instagram, color: 'bg-gradient-to-r from-purple-500 to-pink-500', available: true },
  tiktok: { name: 'TikTok', icon: Music2, color: 'bg-black', available: true },
  facebook: { name: 'Facebook', icon: Facebook, color: 'bg-blue-600', available: true }
};

export const SocialConnections = ({ onUpdate }) => {
  const { t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [manualLink, setManualLink] = useState({ platform: '', username: '', profile_url: '', followers_count: '' });
  const [showManualForm, setShowManualForm] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const res = await fetch(`${API}/social/connections`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setConnections(data);
        if (onUpdate) onUpdate(data);
      }
    } catch (e) {
      console.error('Failed to fetch connections:', e);
    } finally {
      setLoading(false);
    }
  };

  const connectPlatform = async (platform) => {
    try {
      const res = await fetch(`${API}/social/oauth/${platform}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.auth_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to connect');
      }
    } catch (e) {
      toast.error('Connection failed');
    }
  };

  const disconnectPlatform = async (platform) => {
    if (!window.confirm(`Sigur vrei să deconectezi ${PLATFORMS[platform]?.name}?`)) return;
    
    try {
      const res = await fetch(`${API}/social/connections/${platform}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Cont deconectat');
        fetchConnections();
      }
    } catch (e) {
      toast.error('Deconectare eșuată');
    }
  };

  const refreshStats = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/social/refresh`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        const successCount = data.results.filter(r => r.success).length;
        toast.success(`${successCount} conturi actualizate`);
        fetchConnections();
      }
    } catch (e) {
      toast.error('Actualizare eșuată');
    } finally {
      setRefreshing(false);
    }
  };

  const submitManualLink = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/social/link-manual`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(manualLink)
      });
      if (res.ok) {
        toast.success('Cont adăugat (necesită verificare)');
        setManualLink({ platform: '', username: '', profile_url: '', followers_count: '' });
        setShowManualForm(false);
        fetchConnections();
      }
    } catch (e) {
      toast.error('Adăugare eșuată');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getConnection = (platform) => connections.find(c => c.platform === platform);

  if (loading) {
    return <div className="animate-pulse p-4 bg-gray-100 rounded-lg">Se încarcă...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Link2 className="w-5 h-5" />
          Conturi Social Media
        </h3>
        {connections.length > 0 && (
          <Button variant="outline" size="sm" onClick={refreshStats} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualizează
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(PLATFORMS).map(([key, platform]) => {
          const connection = getConnection(key);
          const Icon = platform.icon;

          return (
            <div
              key={key}
              className={`p-4 rounded-lg border ${connection ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${platform.color} text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-medium">{platform.name}</h4>
                    {connection ? (
                      <div className="text-sm text-gray-600">
                        @{connection.username}
                        {connection.verified ? (
                          <CheckCircle className="w-4 h-4 text-green-500 inline ml-1" />
                        ) : connection.manual_entry ? (
                          <Badge variant="outline" className="ml-2 text-xs">Neverificat</Badge>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Neconectat</p>
                    )}
                  </div>
                </div>

                {connection ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => disconnectPlatform(key)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Unlink className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => platform.available ? connectPlatform(key) : setShowManualForm(true)}
                  >
                    Conectează
                  </Button>
                )}
              </div>

              {connection && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="bg-white rounded p-2">
                    <Users className="w-4 h-4 mx-auto text-gray-400" />
                    <p className="text-sm font-semibold">{formatNumber(connection.followers_count)}</p>
                    <p className="text-xs text-gray-500">Urmăritori</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <Video className="w-4 h-4 mx-auto text-gray-400" />
                    <p className="text-sm font-semibold">{formatNumber(connection.posts_count)}</p>
                    <p className="text-xs text-gray-500">Postări</p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <Heart className="w-4 h-4 mx-auto text-gray-400" />
                    <p className="text-sm font-semibold">{connection.engagement_rate?.toFixed(1) || '0'}%</p>
                    <p className="text-xs text-gray-500">Engagement</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual Link Form */}
      <div className="mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowManualForm(!showManualForm)}
          className="text-sm"
        >
          {showManualForm ? 'Anulează' : 'Adaugă manual (fără OAuth)'}
        </Button>

        {showManualForm && (
          <form onSubmit={submitManualLink} className="mt-4 p-4 bg-gray-50 rounded-lg space-y-4">
            <p className="text-sm text-gray-600">
              Adaugă un cont fără autentificare OAuth. Acesta va necesita verificare manuală de către admin.
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Platformă</Label>
                <select
                  className="w-full mt-1 p-2 border rounded"
                  value={manualLink.platform}
                  onChange={(e) => setManualLink({ ...manualLink, platform: e.target.value })}
                  required
                >
                  <option value="">Selectează...</option>
                  {Object.entries(PLATFORMS).map(([key, p]) => (
                    <option key={key} value={key}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Username</Label>
                <Input
                  placeholder="@username"
                  value={manualLink.username}
                  onChange={(e) => setManualLink({ ...manualLink, username: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>URL Profil</Label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={manualLink.profile_url}
                  onChange={(e) => setManualLink({ ...manualLink, profile_url: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Număr Urmăritori</Label>
                <Input
                  type="number"
                  placeholder="10000"
                  value={manualLink.followers_count}
                  onChange={(e) => setManualLink({ ...manualLink, followers_count: e.target.value })}
                />
              </div>
            </div>

            <Button type="submit">Adaugă Cont</Button>
          </form>
        )}
      </div>

      {connections.length > 0 && (
        <p className="text-xs text-gray-500 mt-2">
          Ultima actualizare: {connections[0]?.last_synced_at ? new Date(connections[0].last_synced_at).toLocaleString('ro-RO') : 'N/A'}
        </p>
      )}
    </div>
  );
};

export default SocialConnections;
